import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
} from 'amazon-cognito-identity-js';

let _userPool = null;

function getUserPool() {
  if (_userPool) return _userPool;
  const UserPoolId = import.meta.env.VITE_COGNITO_USER_POOL_ID;
  const ClientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
  if (!UserPoolId || !ClientId) {
    throw new Error('Cognito is not configured. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID.');
  }
  _userPool = new CognitoUserPool({ UserPoolId, ClientId });
  return _userPool;
}

export function getCurrentUser() {
  return getUserPool().getCurrentUser();
}

/** Resolves to a valid CognitoUserSession, or rejects if no session exists. */
export function getSession() {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) return reject(new Error('No current user'));
    user.getSession((err, session) => {
      if (err || !session?.isValid()) return reject(err || new Error('Session invalid'));
      resolve(session);
    });
  });
}

/**
 * Signs in with username/password.
 * Resolves with:
 *   { type: 'SUCCESS' }
 *   { type: 'NEW_PASSWORD_REQUIRED', user: CognitoUser }
 */
export function signIn(username, password) {
  return new Promise((resolve, reject) => {
    const authDetails = new AuthenticationDetails({ Username: username, Password: password });
    const cognitoUser = new CognitoUser({ Username: username, Pool: getUserPool() });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: () => resolve({ type: 'SUCCESS' }),
      onFailure: (err) => reject(err),
      newPasswordRequired: (userAttributes) => {
        // Remove non-writable attributes before completing challenge
        delete userAttributes.email_verified;
        delete userAttributes.phone_number_verified;
        resolve({ type: 'NEW_PASSWORD_REQUIRED', user: cognitoUser });
      },
    });
  });
}

/** Completes the NEW_PASSWORD_REQUIRED challenge for a first-login user. */
export function completeNewPassword(cognitoUser, newPassword) {
  return new Promise((resolve, reject) => {
    cognitoUser.completeNewPasswordChallenge(newPassword, {}, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

/** Changes the password for the currently authenticated user. */
export function changePassword(oldPassword, newPassword) {
  return new Promise((resolve, reject) => {
    const user = getCurrentUser();
    if (!user) return reject(new Error('No current user'));
    user.getSession((sessionErr) => {
      if (sessionErr) return reject(sessionErr);
      user.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) return reject(err);
        resolve(result);
      });
    });
  });
}

export function signOut() {
  const user = getCurrentUser();
  if (user) user.signOut();
}
